/**
 * SEO helpers for dynamic pages (blog posts).
 *
 * Atualiza tags meta, OpenGraph, Twitter Card, canonical e injeta JSON-LD
 * de Article para que Google/Bing indexem corretamente. Funciona em SPA
 * porque crawlers modernos executam JavaScript.
 */

const ensureTag = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

const setMeta = (name, content, useProperty = false) => {
  if (!content) return;
  const attr = useProperty ? 'property' : 'name';
  const sel = `meta[${attr}="${name}"]`;
  const el = ensureTag(sel, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, name);
    return m;
  });
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  const sel = `link[rel="${rel}"]`;
  const el = ensureTag(sel, () => {
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    return l;
  });
  el.setAttribute('href', href);
};

const removeJsonLd = (id) => {
  const existing = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (existing) existing.remove();
};

const injectJsonLd = (id, obj) => {
  removeJsonLd(id);
  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-seo-id', id);
  script.textContent = JSON.stringify(obj);
  document.head.appendChild(script);
};

/**
 * Aplica metadados SEO completos a partir de um objeto descritivo.
 */
export const applySeo = ({
  title,
  description,
  keywords,
  canonical,
  image,
  imageWidth = 1200,
  imageHeight = 630,
  imageAlt,
  type = 'website',
  publishedAt,
  updatedAt,
  author,
  siteName = 'nfcesysPdv',
  locale = 'pt_BR',
  twitterCard = 'summary_large_image',
  jsonLd, // optional extra schema object
  jsonLdId = 'page-jsonld',
  breadcrumbs, // optional array [{name, url}]
}) => {
  if (title) document.title = title;
  setMeta('description', description);
  if (keywords && keywords.length) {
    setMeta('keywords', Array.isArray(keywords) ? keywords.join(', ') : keywords);
  }

  // Open Graph
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:type', type, true);
  setMeta('og:url', canonical, true);
  setMeta('og:site_name', siteName, true);
  setMeta('og:locale', locale, true);
  if (image) {
    setMeta('og:image', image, true);
    setMeta('og:image:width', String(imageWidth), true);
    setMeta('og:image:height', String(imageHeight), true);
    if (imageAlt || title) setMeta('og:image:alt', imageAlt || title, true);
  }

  // Twitter Card
  setMeta('twitter:card', twitterCard);
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  if (image) setMeta('twitter:image', image);

  // Article-specific (apenas para type=article)
  if (publishedAt) setMeta('article:published_time', publishedAt, true);
  if (updatedAt) setMeta('article:modified_time', updatedAt, true);
  if (author) setMeta('article:author', author, true);

  setLink('canonical', canonical);

  if (jsonLd) injectJsonLd(jsonLdId, jsonLd);

  // Schema.org BreadcrumbList (opcional)
  if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    injectJsonLd('breadcrumb-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: b.url,
      })),
    });
  } else {
    removeJsonLd('breadcrumb-jsonld');
  }
};

/**
 * Restaura o estado padrão da home (chamado quando saímos de uma página de artigo).
 */
export const resetSeoToDefault = () => {
  document.title = 'Sistema PDV + NFC-e Online | nfcesysPdv (Teste 30 dias)';
  setMeta(
    'description',
    'Sistema PDV completo com emissor de NFC-e homologado SEFAZ. Controle de estoque, WhatsApp Bot e balanças. Teste grátis 30 dias.'
  );
  setLink('canonical', 'https://nfcesys.com.br/');
  removeJsonLd('page-jsonld');
  removeJsonLd('article-jsonld');
  removeJsonLd('breadcrumb-jsonld');
  removeJsonLd('faq-jsonld');
};
